import React, { FC, useState } from 'react'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { GiveawayPartnerLogo, Maybe } from '@/types/codegen-contentful'

interface LightboxProps {
  showImages: Maybe<GiveawayPartnerLogo>[]
  currentImageIndex: number
  setIsLightboxOpen: (isOpen: boolean) => void
  setCurrentImageIndex: (index: number) => void
}

export const Lightbox: FC<LightboxProps> = ({
  showImages,
  currentImageIndex,
  setIsLightboxOpen,
  setCurrentImageIndex,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  const handleBackgroundClick = () => {
    closeLightbox()
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const goToPrevious = () => {
    const newIndex = currentImageIndex === 0 ? showImages.length - 1 : currentImageIndex - 1
    setCurrentImageIndex(newIndex)
  }

  const goToNext = () => {
    const newIndex = currentImageIndex === showImages.length - 1 ? 0 : currentImageIndex + 1
    setCurrentImageIndex(newIndex)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0]?.clientX ?? null)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const swipeDistance = touchStart - touchEnd
    const swipeThreshold = 50

    if (swipeDistance > swipeThreshold) {
      goToNext()
    }

    if (swipeDistance < -swipeThreshold) {
      goToPrevious()
    }

    setTouchStart(null)
    setTouchEnd(null)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
      id="lightbox-background"
      onClick={handleBackgroundClick}
    >
      <button className="absolute right-4 top-4" onClick={closeLightbox}>
        <CloseIcon color="core-gray-100" size={24} />
      </button>
      <div
        className="relative"
        onClick={handleContentClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="aspect-h-9 aspect-w-16 relative w-[90vw]">
          <Image
            src={showImages[currentImageIndex]?.imageUrl || ''}
            alt={showImages[currentImageIndex]?.altText || ''}
            fill
            className="object-contain object-center"
          />
        </div>

        <button className="absolute left-1 top-1/2 -translate-y-1/2 text-3xl lg:-left-7" onClick={goToPrevious}>
          <CaretLeftIcon size={24} />
        </button>

        <button className="absolute right-1 top-1/2 -translate-y-1/2 text-3xl lg:-right-7" onClick={goToNext}>
          <CaretRightIcon size={24} />
        </button>
      </div>
    </div>
  )
}
